import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Minus, ArrowRight } from 'lucide-react';
import { PublicLayout, Reveal } from '@/components/public';
import { BRAND } from '@/config/brand';
import { PACKAGES, FEATURE_GROUPS, PROCESSING_NOTE } from '@/config/packages';
import HomeBusinessCard from './join/HomeBusinessCard';

const NAV_PROPS = {
  links: [],
  primaryCta: { href: '/', label: 'Become a partner', testId: 'features-nav-join-cta' },
};

const FOOTER_PROPS = {
  tagline:
    'A Canadian-owned delivery platform that keeps more of every sale with the local businesses that feed our communities.',
  columns: [
    {
      title: 'For Restaurants',
      links: [
        { href: '/', label: 'Why Hungry Canadian', testId: 'features-footer-why' },
        { href: '/tools', label: 'Tools', testId: 'features-footer-tools' },
        { href: '/features', label: 'Features', testId: 'features-footer-features' },
        { href: '/integrations', label: 'Integrations', testId: 'features-footer-integrations' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { href: 'https://help.hungrycanadian.com/', label: 'Help center', external: true, testId: 'features-footer-help' },
        { href: `mailto:${BRAND.merchantsEmail}`, label: 'Contact merchants', external: true, testId: 'features-footer-contact' },
      ],
    },
    {
      title: 'Company',
      links: [{ href: BRAND.website, label: 'Main site', external: true, testId: 'features-footer-main' }],
    },
  ],
  legal: [
    { href: 'https://help.hungrycanadian.com/tos/', label: 'Terms of Service', external: true, testId: 'features-footer-tos' },
    { href: 'https://help.hungrycanadian.com/privacypolicy/', label: 'Privacy Policy', external: true, testId: 'features-footer-privacy' },
  ],
};

function Cell({ value }) {
  if (value === true) return <Check className="w-5 h-5 text-primary mx-auto" strokeWidth={2.5} />;
  if (value === false) return <Minus className="w-4 h-4 text-muted-foreground/50 mx-auto" />;
  return <span className="text-sm font-medium text-foreground">{value}</span>;
}

export default function FeaturesPage() {
  return (
    <PublicLayout navProps={NAV_PROPS} footerProps={FOOTER_PROPS}>
      {/* Hero */}
      <section className="py-12 md:py-16 px-4 sm:px-6 bg-card border-b border-border" data-testid="features-page">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span aria-hidden className="h-4 w-1 bg-primary" />
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-primary">Packages</p>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-black tracking-tight text-foreground leading-[1.05]">
            Compare every feature, package by package.
          </h1>
          <p className="mt-4 text-muted-foreground text-sm md:text-base leading-relaxed">
            Month-to-month plans, 0% commission on food sales, and feature access that scales with your business.
          </p>
        </div>
      </section>

      {/* Comparison matrix */}
      <section className="py-10 md:py-14 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="max-h-[70vh] md:max-h-[75vh] overflow-auto border border-border" data-testid="features-matrix">
              <table className="w-full min-w-[600px] border-collapse">
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-30 bg-card border-b border-border text-left align-bottom p-3 md:p-4 min-w-[150px] md:min-w-[220px]" />
                    {PACKAGES.map((pkg) => (
                      <th
                        key={pkg.id}
                        className="sticky top-0 z-20 bg-card border-b border-l border-border p-3 md:p-4 text-center align-bottom min-w-[110px] md:min-w-[140px]"
                        data-testid={`features-col-${pkg.id}`}
                      >
                        {pkg.badge && (
                          <span className="inline-block mb-1.5 bg-primary text-primary-foreground text-[9px] md:text-[10px] font-bold px-2 py-0.5 uppercase tracking-wide">
                            {pkg.badge}
                          </span>
                        )}
                        <p className="font-heading font-black text-foreground leading-tight text-sm md:text-base">{pkg.name}</p>
                        <p className="hidden md:block text-[11px] text-muted-foreground mt-0.5">{pkg.tagline}</p>
                        <p className="mt-1.5 md:mt-2 font-heading font-black text-xl md:text-2xl text-foreground leading-none">
                          {pkg.priceLabel}
                          <span className="text-xs text-muted-foreground font-medium">{pkg.sub}</span>
                        </p>
                        <p className="hidden md:block text-[10px] text-muted-foreground mt-1">{pkg.setup}</p>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_GROUPS.map((group) => (
                    <React.Fragment key={group.group}>
                      <tr className="bg-secondary">
                        <td
                          colSpan={PACKAGES.length + 1}
                          className="sticky left-0 bg-secondary px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-primary"
                        >
                          {group.group}
                        </td>
                      </tr>
                      {group.rows.map((row) => (
                        <tr key={row.label} className="border-b border-border last:border-0">
                          <td className="sticky left-0 z-10 bg-background p-4 align-top">
                            <p className="text-sm font-medium text-foreground">{row.label}</p>
                            {row.hint && <p className="text-[11px] text-muted-foreground mt-0.5">{row.hint}</p>}
                          </td>
                          {row.values.map((value, idx) => (
                            <td key={PACKAGES[idx].id} className="p-4 text-center align-middle border-l border-border">
                              <Cell value={value} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <p className="mt-5 text-xs text-muted-foreground leading-relaxed max-w-3xl" data-testid="features-processing-note">
              {PROCESSING_NOTE}
            </p>
          </Reveal>

          {/* Home Business — full-width card matching the pricing section pattern */}
          <div className="mt-10 md:mt-14">
            <HomeBusinessCard testIdPrefix="features-home-business" />
          </div>

          <Reveal delay={0.12}>
            <div className="mt-10 text-center">
              <Link
                to="/"
                data-testid="features-cta-join"
                className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary-hover transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
              >
                Become a partner <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </PublicLayout>
  );
}
